import { createInput, createSelect, MountTarget, newDialog, readConfig } from './utils/elements';

export const FLEX_CONTAINER_EDIT_SELECTOR = '.cds-lrn-flex-container';

interface FlexContainerConfig {
  withGap: boolean;
  withWrap: boolean;
  isColumn: boolean;
  justify: string;
  align: string;
}

export function flexContainerDialog(
  insertHtml: (html: string) => void,
  mountTarget?: MountTarget,
  existingEl?: HTMLElement
) {
  const dialog = newDialog('Flex container maken', () => insertHtml(''), mountTarget);

  const withGap = createInput('withGap', 'Met tussenruimte', 'checkbox');
  const withWrap = createInput('withWrap', 'Met wrap', 'checkbox');
  const isColumn = createInput('isColumn', 'Onder elkaar', 'checkbox');

  const horInput = createSelect('justify', 'Horizontaal', [
    { label: 'Links', value: 'justify-start' },
    { label: 'Midden', value: 'justify-center' },
    { label: 'Rechts', value: 'justify-end' },
  ]);
  const verInput = createSelect('align', 'Verticaal', [
    { label: 'Boven', value: 'items-start' },
    { label: 'Midden', value: 'items-center' },
    { label: 'Onder', value: 'items-end' },
  ]);

  if (existingEl) {
    const cfg = readConfig<FlexContainerConfig>(existingEl, { withGap: false, withWrap: false, isColumn: false, justify: '', align: '' });
    withGap.setChecked(!!cfg.withGap);
    withWrap.setChecked(!!cfg.withWrap);
    isColumn.setChecked(!!cfg.isColumn);
    if (cfg.justify) horInput.setValue(cfg.justify);
    if (cfg.align) verInput.setValue(cfg.align);
  }

  dialog
    .querySelector('#cds-dialog-content')
    .append(withGap.field, withWrap.field, isColumn.field, horInput.field, verInput.field);

  dialog.addEventListener('onSubmit', () => {
    const config: FlexContainerConfig = {
      withGap: withGap.checked(),
      withWrap: withWrap.checked(),
      isColumn: isColumn.checked(),
      justify: horInput.value(),
      align: verInput.value(),
    };
    const html = makeFlexContainer(config);
    if (html) insertHtml(html);
  });
}

function makeFlexContainer(config: FlexContainerConfig) {
  if (!config.justify || !config.align) return;

  const container = document.createElement('div');
  container.classList.add('cds-lrn-flex-container', 'cds-lrn-editor-container', 'flex');
  container.setAttribute('data-cb-config', JSON.stringify(config));

  if (config.withGap) container.classList.add('gap-xs');
  if (config.withWrap) container.classList.add('flex-wrap');
  if (config.isColumn) container.classList.add('flex-column');

  container.classList.add(config.justify, config.align);

  return container.outerHTML;
}

export function containerItemDialog(insertHtml: (html: string) => void, mountTarget?: MountTarget) {
  const focusNode = document.getSelection()?.focusNode;
  const element = focusNode instanceof Element ? focusNode : focusNode?.parentElement;
  const container = element?.closest('.cds-lrn-flex-container.cds-lrn-editor-container');

  // Learnosity disables the editor until the callback runs, so every path below must call insertHtml.
  if (!container || !element) {
    insertHtml('');
    showContainerHint(mountTarget);
    return;
  }

  const currentItem = findCurrentItem(element, container);

  if (currentItem) {
    // Items may not nest: inserting at the cursor would land inside the current item,
    // so place the new item directly after it in the container instead.
    currentItem.insertAdjacentHTML('afterend', makeContainerItem());
    insertHtml('');
    return;
  }

  insertHtml(makeContainerItem());
}

function findCurrentItem(element: Element, container: Element): Element | null {
  let node: Element | null = element;
  while (node && node !== container && node.parentElement !== container) {
    node = node.parentElement;
  }

  return node && node !== container && node.classList.contains('cds-lrn-editor-item') ? node : null;
}

function makeContainerItem(): string {
  const item = document.createElement('div');
  item.innerHTML = '<div>placeholder</div> <div>placeholder</div>';
  item.classList.add('cds-lrn-editor-item', 'flex', 'flex-column', 'justify-center', 'items-center');

  return item.outerHTML;
}

function showContainerHint(mountTarget: MountTarget = document.body) {
  const dialog = document.createElement('dcr-dialog');
  dialog.setAttribute('headline', 'Container item invoegen');
  dialog.setAttribute('type', 'acknowledge');
  dialog.setAttribute(
    'supporting-text',
    'Plaats de cursor eerst in een flex container en probeer daarna opnieuw een container item in te voegen.'
  );
  dialog.setAttribute('open', '');

  dialog.addEventListener('closed', () => {
    dialog.remove();
  });

  const mount = typeof mountTarget === 'string' ? document.querySelector(mountTarget) : mountTarget;
  (mount ?? document.body).append(dialog);
}
